import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaGift,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useAnnouncements } from "../../context/AnnouncementContext";

const AnnouncementBar = ({ isDarkMode }) => {
  const {
    barAnnouncements = [],
    dismissAnnouncement,
    markAsSeen,
  } = useAnnouncements();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localDismissed, setLocalDismissed] = useState([]);

  // Filter out locally dismissed announcements
  const visibleAnnouncements = (barAnnouncements || []).filter(
    (ann) => !localDismissed.includes(ann._id),
  );

  const currentAnnouncement = visibleAnnouncements[currentIndex];

  useEffect(() => {
    if (currentAnnouncement) {
      markAsSeen(currentAnnouncement._id);
    }
  }, [currentAnnouncement, markAsSeen]);

  const handleDismiss = (announcementId) => {
    setLocalDismissed([...localDismissed, announcementId]);
    dismissAnnouncement(announcementId);
    if (
      currentIndex >= visibleAnnouncements.length - 1 &&
      visibleAnnouncements.length > 1
    ) {
      setCurrentIndex(0);
    }
  };

  const nextAnnouncement = () => {
    if (currentIndex < visibleAnnouncements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const prevAnnouncement = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(visibleAnnouncements.length - 1);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      info: <FaInfoCircle size={14} />,
      success: <FaCheckCircle size={14} />,
      warning: <FaExclamationTriangle size={14} />,
      emergency: <FaExclamationTriangle size={14} />,
      event: <FaCalendarAlt size={14} />,
      promotion: <FaGift size={14} />,
    };
    return icons[type] || icons.info;
  };

  const getTypeBg = (type) => {
    const bgColors = {
      info: "#3b82f6",
      success: "#10b981",
      warning: "#f59e0b",
      emergency: "#ef4444",
      event: "#8b5cf6",
      promotion: "#ec4899",
    };
    return bgColors[type] || bgColors.info;
  };

  // Don't render if no announcements
  if (
    !visibleAnnouncements ||
    visibleAnnouncements.length === 0 ||
    !currentAnnouncement
  ) {
    return null;
  }

  const typeBg = getTypeBg(currentAnnouncement.type);
  const typeIcon = getTypeIcon(currentAnnouncement.type);
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBgColor = () => (isDarkMode ? "#1e1e2d" : "#ffffff");

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        style={{
          position: "relative",
          background: getBgColor(),
          borderBottom: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
          zIndex: 999,
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: "32px",
                height: "32px",
                background: `${typeBg}20`,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: typeBg,
              }}
            >
              {typeIcon}
            </div>

            {/* Navigation Arrows */}
            {visibleAnnouncements.length > 1 && (
              <button
                onClick={prevAnnouncement}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: getTextColor(),
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FaChevronLeft size={12} />
              </button>
            )}

            {/* Content */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    background: typeBg,
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                >
                  {currentAnnouncement.type?.toUpperCase() || "INFO"}
                </span>
                <strong style={{ fontSize: "13px", color: getTextColor() }}>
                  {currentAnnouncement.title}
                </strong>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  margin: "4px 0 0",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {currentAnnouncement.message?.length > 120
                  ? currentAnnouncement.message.substring(0, 120) + "..."
                  : currentAnnouncement.message}
              </p>
              {currentAnnouncement.targetAudience !== "all" && (
                <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                  {currentAnnouncement.targetBranch && (
                    <span
                      style={{
                        fontSize: "9px",
                        color: "#8b5cf6",
                        background: `${typeBg}15`,
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      Branch: {currentAnnouncement.targetBranch}
                    </span>
                  )}
                  {currentAnnouncement.targetSemester && (
                    <span
                      style={{
                        fontSize: "9px",
                        color: "#8b5cf6",
                        background: `${typeBg}15`,
                        padding: "2px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      Semester: {currentAnnouncement.targetSemester}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Arrows Right */}
            {visibleAnnouncements.length > 1 && (
              <button
                onClick={nextAnnouncement}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: getTextColor(),
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FaChevronRight size={12} />
              </button>
            )}

            {/* Pagination Indicator */}
            {visibleAnnouncements.length > 1 && (
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                  }}
                >
                  {currentIndex + 1}/{visibleAnnouncements.length}
                </span>
              </div>
            )}

            {/* Link Button */}
            {currentAnnouncement.link && (
              <button
                onClick={() => window.open(currentAnnouncement.link, "_blank")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  background: typeBg,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "11px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                {currentAnnouncement.linkText || "Learn More"}
                <FaExternalLinkAlt size={10} />
              </button>
            )}

            {/* Dismiss Button */}
            <button
              onClick={() => handleDismiss(currentAnnouncement._id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: isDarkMode ? "#94a3b8" : "#64748b",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaTimes size={14} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnnouncementBar;
